import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Truck,
  Upload,
  Edit,
  Plus,
  Calendar,
  Building,
  CreditCard,
  Users
} from 'lucide-react';
import AddEntregaModal from './AddEntregaModal';
import AddRecojoModal from './AddRecojoModal';
import AddMasivoModal from './AddMasivoModal';
import EditUserModal from './EditUserModal';
import UserDeliveriesList from './UserDeliveriesList';
import UserOrdersList from './UserOrdersList';

const API_URL = process.env.REACT_APP_API_URL;

function UserDetailModal({ userId, onClose }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // 'entrega', 'recojo', 'masivo', 'edit'
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'master':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'driver':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'user':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getRoleLabel = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Administrador';
      case 'master':
        return 'Master';
      case 'driver':
        return 'Conductor';
      case 'user':
        return 'Cliente';
      default:
        return role || 'Usuario';
    }
  };

  const handleViewDetails = (itemId, itemType) => {
    setSelectedItemId(itemId);
    setSelectedItemType(itemType);
    // This will trigger the DetailModal to open
    // You can emit a custom event or use a callback prop
    const event = new CustomEvent('openDetailModal', {
      detail: { id: itemId, type: itemType }
    });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-slate-600 font-montserrat">Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2 font-montserrat">
              Error al cargar usuario
            </h3>
            <p className="text-sm text-slate-600 mb-4 font-montserrat">
              No se pudo cargar la información del usuario.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#B1336E] text-white rounded-lg font-medium hover:bg-[#B1336E]/90 transition-colors font-montserrat"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 p-6 border-b border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {user.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt={user.username}
                      className="w-20 h-20 rounded-full object-cover border-4 border-slate-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center border-4 border-slate-200">
                      <span className="text-white font-bold text-3xl font-montserrat">
                        {user.username?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-slate-800 font-montserrat">
                      {user.username || 'Usuario sin nombre'} {user.id}
                    </h2>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full border font-montserrat ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {user.email && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail className="w-4 h-4" />
                        <span className="font-montserrat">{user.email}</span>
                      </div>
                    )}
                    {user.phone_number && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4" />
                        <span className="font-montserrat">{user.phone_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Action Buttons */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 font-montserrat">
                Acciones Rápidas
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => setActiveModal('entrega')}
                  className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-[#B1336E]/10 to-[#B1336E]/5 hover:from-[#B1336E]/20 hover:to-[#B1336E]/10 border-2 border-[#B1336E]/20 rounded-xl transition-all group"
                >
                  <div className="p-2 bg-[#B1336E] rounded-lg group-hover:scale-110 transition-transform">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Agregar Entrega
                  </span>
                </button>

                <button
                  onClick={() => setActiveModal('recojo')}
                  className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-[#E6B43C]/10 to-[#E6B43C]/5 hover:from-[#E6B43C]/20 hover:to-[#E6B43C]/10 border-2 border-[#E6B43C]/20 rounded-xl transition-all group"
                >
                  <div className="p-2 bg-[#E6B43C] rounded-lg group-hover:scale-110 transition-transform">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Agregar Recojo
                  </span>
                </button>

                <button
                  onClick={() => setActiveModal('masivo')}
                  className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-[#67A43C]/10 to-[#67A43C]/5 hover:from-[#67A43C]/20 hover:to-[#67A43C]/10 border-2 border-[#67A43C]/20 rounded-xl transition-all group"
                >
                  <div className="p-2 bg-[#67A43C] rounded-lg group-hover:scale-110 transition-transform">
                    <Upload className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Carga Masiva
                  </span>
                </button>

                <button
                  onClick={() => setActiveModal('edit')}
                  className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-slate-500/10 to-slate-500/5 hover:from-slate-500/20 hover:to-slate-500/10 border-2 border-slate-300 rounded-xl transition-all group"
                >
                  <div className="p-2 bg-slate-600 rounded-lg group-hover:scale-110 transition-transform">
                    <Edit className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 font-montserrat">
                    Editar Datos
                  </span>
                </button>
              </div>
            </div>

            {/* User Details Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Bank Information */}
              {(user.bank_account || user.bank_name) && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-[#B1336E]" />
                    <h4 className="font-semibold text-slate-800 font-montserrat">
                      Información Bancaria
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {user.bank_name && (
                      <div>
                        <span className="text-slate-500 font-montserrat">Banco:</span>
                        <p className="font-medium text-slate-700 font-montserrat">{user.bank_name}</p>
                      </div>
                    )}
                    {user.name_on_bank_account && (
                      <div>
                        <span className="text-slate-500 font-montserrat">Titular:</span>
                        <p className="font-medium text-slate-700 font-montserrat">{user.name_on_bank_account}</p>
                      </div>
                    )}
                    {user.bank_account && (
                      <div>
                        <span className="text-slate-500 font-montserrat">Cuenta:</span>
                        <p className="font-medium text-slate-700 font-mono">{user.bank_account}</p>
                      </div>
                    )}
                    {user.interbancario_bank_account && (
                      <div>
                        <span className="text-slate-500 font-montserrat">CCI:</span>
                        <p className="font-medium text-slate-700 font-mono">{user.interbancario_bank_account}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Provider Information */}
              {user.provider_id && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-5 h-5 text-[#E6B43C]" />
                    <h4 className="font-semibold text-slate-800 font-montserrat">
                      Proveedor Asociado
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-slate-500 font-montserrat">ID Proveedor:</span>
                      <p className="font-medium text-slate-700 font-montserrat">#{user.provider_id}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Referral Information */}
              {(user.referral_code || user.referred_by_user_id) && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-[#67A43C]" />
                    <h4 className="font-semibold text-slate-800 font-montserrat">
                      Referidos
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {user.referral_code && (
                      <div>
                        <span className="text-slate-500 font-montserrat">Código de Referido:</span>
                        <p className="font-medium text-slate-700 font-mono">{user.referral_code}</p>
                      </div>
                    )}
                    {user.referred_by_user_id && (
                      <div>
                        <span className="text-slate-500 font-montserrat">Referido por:</span>
                        <p className="font-medium text-slate-700 font-montserrat">Usuario #{user.referred_by_user_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Addresses */}
              {user.addresses && user.addresses.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 md:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-[#B1336E]" />
                    <h4 className="font-semibold text-slate-800 font-montserrat">
                      Direcciones Guardadas
                    </h4>
                    <span className="text-xs bg-[#B1336E]/10 text-[#B1336E] px-2 py-0.5 rounded-full font-medium font-montserrat">
                      {user.addresses.length}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                    {user.addresses.map((address, idx) => (
                      <div key={address.id || idx} className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-sm font-medium text-slate-700 font-montserrat">
                          {address.label || `Dirección ${idx + 1}`}
                        </p>
                        <p className="text-sm text-slate-600 font-montserrat">
                          {address.address || 'Sin dirección especificada'}
                        </p>
                        {address.district && (
                          <p className="text-xs text-slate-500 mt-1 font-montserrat">
                            📍 {address.district}
                          </p>
                        )}
                        {address.reference && (
                          <p className="text-xs text-slate-500 mt-1 font-montserrat">
                            🏠 Ref: {address.reference}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliveries List */}
              {user.delivery_points && user.delivery_points.length > 0 && (
                <div className="md:col-span-2">
                  <UserDeliveriesList 
                    deliveries={user.delivery_points}
                    onViewDetails={handleViewDetails}
                  />
                </div>
              )}

              {/* Orders List */}
              {user.order_points && user.order_points.length > 0 && (
                <div className="md:col-span-2">
                  <UserOrdersList 
                    orders={user.order_points}
                    onViewDetails={handleViewDetails}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Modals */}
      {activeModal === 'entrega' && (
        <AddEntregaModal
          userId={userId}
          user={user}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            // Optionally refresh data
          }}
        />
      )}

      {activeModal === 'recojo' && (
        <AddRecojoModal
          userId={userId}
          user={user}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            // Optionally refresh data
          }}
        />
      )}

      {activeModal === 'masivo' && (
        <AddMasivoModal
          userId={userId}
          user={user}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            // Optionally refresh data
          }}
        />
      )}

      {activeModal === 'edit' && (
        <EditUserModal
          userId={userId}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            fetchUserDetails(); // Refresh user data after edit
          }}
        />
      )}
    </>
  );
}

export default UserDetailModal;
